// File: styled.js
import styled from 'styled-components';
import { Card, Form, Radio } from 'antd';
import { COLORS } from '../../../../../../../constants/colors';

// Helper to get the card's shadow style.
// Used by both ProductCard and SimpleProductCard:
const getShadowStyle = (theme) => {
  if (!theme?.shadow_color) return '0 4px 8px rgba(0, 0, 0, 0.1)';

  const hexToRgba = (hex, alpha = 1) => {
    const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const softShadowColor = hexToRgba(theme.shadow_color, 0.1);
  const hardShadowColor = hexToRgba(theme.shadow_color, 0.5);

  switch (theme?.card_style) {
    case 'soft-shadow-squared':
    case 'soft-shadow-slightly-rounded':
      return `0 6px 12px ${softShadowColor}`;
    case 'hard-shadow-squared':
    case 'hard-shadow-slightly-rounded':
      return `3px 3px 0 ${hardShadowColor}`;
    case 'outline-squared':
    case 'outline-slightly-rounded':
      return 'none'; // No shadow for ghost buttons
    default:
      return `0 4px 8px ${softShadowColor}`;
  }
};

/* ─────────────────────────────────────────────────────────────
   1) Card styles for "spotlight" type (used by item.cardType === 'spotlight')
   ───────────────────────────────────────────────────────────── */
export const ProductCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 15px;
  margin-bottom: 20px;
  margin-left: auto;
  margin-right: auto;
  box-shadow: ${({ theme }) => getShadowStyle(theme)};
  border-radius: ${({ theme }) =>
    theme?.card_style?.includes('slightly-rounded') ? '10px'
      : theme?.card_style?.includes('squared') ? '0'
        : '15px'};
  border: ${({ theme }) =>
    theme?.card_style?.includes('outline')
      ? `2px solid ${theme?.card_color || '#000'}`
      : 'none'};
  background-color: ${({ theme }) =>
    theme?.card_style?.includes('outline')
      ? 'transparent'
      : theme?.card_color || '#1c1c1c'};
  text-align: center;

  .product-content {
    display: flex;
    width: 100%;
    align-items: flex-start;
    margin-bottom: 10px;
  }

  .product-info {
    flex-grow: 1;
    padding-left: 15px;
    text-align: left;
  }

  .product-image {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 80px;
    height: 80px;
    border-radius: 10px;
    overflow: hidden;
  }

  h3 {
    margin: 0;
    font-size: 16px;
    color: ${({ theme }) => theme?.card_text_color || 'white'};
  }

  p {
    margin: 5px 0;
    color: ${({ theme }) => theme?.card_text_color || '#d3d3d3'};
  }

  .product-price {
    font-size: 14px;
    color: ${({ theme }) => theme?.card_text_color || '#000'};

    .amount {
      font-weight: 600;
    }
  }

  .product-button {
    margin-top: auto;
    width: 100%;
    min-height: 55px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;
    text-decoration: none;

    border-radius: ${({ theme }) => {
    if (theme?.button_style?.includes('completely-rounded')) return '27.5px';
    if (theme?.button_style?.includes('slightly-rounded')) return '10px';
    return '0';
  }};

    background-color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? 'transparent'
      : theme?.button_color || COLORS.$mainBlue} !important;

    border: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? `2px solid ${theme?.card_color || COLORS.$mainBlue}`
      : 'none'} !important;

    color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? theme?.button_color || COLORS.$mainBlue
      : theme?.button_text_color || '#FFFFFF'} !important;

    transition: all 0.3s ease;

    &:hover {
      background-color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? theme?.button_color || COLORS.$mainBlue
      : `${theme?.button_color || COLORS.$mainBlue}dd`} !important;
      color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? 'transparent'
      : theme?.button_text_color || '#FFFFFF'} !important;
    }

    &:focus, &:active {
      background-color: ${({ theme }) =>
    theme?.button_style?.includes('outline') ? 'transparent'
      : theme?.button_color || COLORS.$mainBlue} !important;
      color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? theme?.button_color || COLORS.$mainBlue
      : theme?.button_text_color || '#FFFFFF'} !important;
      border-color: ${({ theme }) => theme?.button_color || COLORS.$mainBlue} !important;
    }
  }

  .button-text {
    flex-grow: 1;
    text-align: left;
    color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? theme?.button_color || COLORS.$mainBlue
      : theme?.button_text_color || '#FFFFFF'} !important;
    transition: color 0.3s ease;

    ${({ theme }) => theme?.button_style?.includes('outline') && `
      .product-button:hover & {
        color: ${theme?.button_text_color || '#FFFFFF'} !important;
      }
    `}
  }

  .product-button:hover .button-text {
    color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? theme?.button_text_color || '#FFFFFF'
      : theme?.button_text_color || '#FFFFFF'} !important;
  }

  .button-icon {
    margin-left: 10px;
    display: flex;
    align-items: center;
    transition: color 0.3s ease;

    svg {
      path {
        transition: stroke 0.3s ease;
        stroke: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? theme?.button_color || COLORS.$mainBlue
      : theme?.button_text_color || '#FFFFFF'} !important;
      }
    }
  }

  .product-button:hover .button-icon {
    svg {
      path {
        stroke: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? theme?.button_text_color || '#FFFFFF'
      : theme?.button_text_color || '#FFFFFF'} !important;
      }
    }
  }

  .ant-card-body {
    padding: 0px;
    width: 100%;
    border-radius: 15px;
  }
`;

/* ─────────────────────────────────────────────────────────────
   2) Simplified "button" type (used by item.cardType === 'button')
   ───────────────────────────────────────────────────────────── */
export const SimpleProductCard = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
  padding: 10px;
  margin-left: auto;
  margin-right: auto;
  box-shadow: ${({ theme }) => getShadowStyle(theme)};
  border-radius: ${({ theme }) =>
    theme?.card_style?.includes('slightly-rounded')
      ? '10px'
      : theme?.card_style?.includes('squared')
        ? '0'
        : '17px'};
  border: ${({ theme }) =>
    theme?.card_style?.includes('outline')
      ? `2px solid ${theme?.card_color || '#000'}`
      : 'none'};
  background-color: ${({ theme }) =>
    theme?.card_style?.includes('outline')
      ? 'transparent'
      : theme?.card_color || '#f3f3f1'};
  cursor: pointer;
  transition: transform 0.2s;
  height: 75px;

  &:hover {
    transform: scale(1.02);
  }

  .block__card--pill {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 10px;
    border: none;
    border-radius: ${({ theme }) =>
    theme?.card_style?.includes('slightly-rounded')
      ? '10px'
      : theme?.card_style?.includes('squared')
        ? '0'
        : '17px'};
    text-decoration: none;
    color: inherit;

    &:hover {
      background-color: ${({ theme }) =>
    theme?.card_style?.includes('outline')
      ? 'rgba(0,0,0,0.1)'
      : theme?.card_color
        ? `${theme.card_color}dd`
        : '#e0e0e0'};
    }
  }

  .block__image--pill {
    margin-right: 15px;
    display: flex;
    align-items: center;

    .base-preview-image {
      width: 55px;
      height: 55px;
      border-radius: 10px;
      object-fit: cover;
    }
  }

  .block__content {
    flex: 1;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .block__spacer {
    width: 70px;
    height: 70px;
  }

  .block__text--pill {
    font-size: 14px;
    color: ${({ theme }) => theme?.card_text_color || 'black'};
  }

  .price-review {
    display: flex;
    align-items: center;

    .product-price {
      font-size: 14px;
      color: ${({ theme }) => theme?.card_text_color || '#000'};

      .amount {
        font-weight: 600;
      }
    }
  }
`;

/* ─────────────────────────────────────────────────────────────
   3) TIP PREVIEW Card, images, and buttons
   ───────────────────────────────────────────────────────────── */
export const PreviewCard = styled.div`
  padding: 17px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);

  background-color: ${({ theme }) =>
    theme?.card_style?.includes('outline')
      ? 'transparent'
      : theme?.card_color || '#fff'
  };

  border: ${({ theme }) =>
    theme?.card_style?.includes('outline')
      ? `2px solid ${theme?.card_color || '#000'}`
      : 'none'
  };

  border-radius: ${({ theme }) =>
    theme?.card_style?.includes('slightly-rounded') ? '10px'
      : theme?.card_style?.includes('squared') ? '0'
        : '15px'
  };
`;

export const PreviewImage = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 12px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const PreviewContent = styled.div`
  /* Additional styling can go here if needed */
`;

export const TipButtonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

// Helper for making tip buttons rounded/outlined:
const getButtonRadius = (buttonStyle) => {
  if (!buttonStyle) return '12px'; // fallback
  if (buttonStyle.includes('slightly-rounded')) return '10px';
  if (buttonStyle.includes('completely-rounded')) return '27.5px';
  if (buttonStyle.includes('square') || buttonStyle.includes('squared')) return '0px';
  return '12px';
};

export const TipButton = styled(Radio.Button).attrs((props) => ({
  theme: {
    button_color: props.theme?.button_color || '#FFD700',
    button_text_color: props.theme?.button_text_color || '#212121',
    button_style: props.theme?.button_style || 'fill-slightly-rounded',
  },
}))`
  height: auto !important;
  padding: 35px 15px !important;
  text-align: center;
  border-radius: ${({ theme }) => getButtonRadius(theme.button_style)} !important;
  transition: all 0.3s ease;

  background-color: ${({ theme }) =>
    theme.button_style.includes('outline')
      ? 'transparent'
      : theme.button_color}66 !important;
 border: 2px solid
   ${({ theme }) => theme.button_style.includes('outline')
    ? theme.button_color
    : 'transparent'} !important;
  color: ${({ theme }) =>
    theme.button_style.includes('outline')
      ? theme.button_color
      : theme.button_text_color} !important;

  &.ant-radio-button-wrapper {
    &:before {
      display: none;
    }

    &:hover {
    border: 3px solid
       ${({ theme }) => theme.button_color || '#FFD700'} !important;
    }

    &.ant-radio-button-wrapper-checked {
      border: 3px solid
       ${({ theme }) => theme.button_color || '#FFD700'} !important;
      border-color: ${({ theme }) => theme.button_color || '#FFD700'} !important;
     
      color: ${({ theme }) =>
    theme.button_style.includes('outline')
      ? theme.button_color
      : theme.button_text_color} !important;
    }
  }

  .amount {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 4px;
  }
  .label {
    font-size: 14px;
    color: #6B7280;
  }
`;

export const CustomAmountInput = styled(Form.Item)`
  margin-top: 24px;
  .ant-input {
    padding: 12px;
    border-radius: 8px;
    border: 1px solid #E5E7EB;
    &:hover {
      border-color: ${COLORS.$mainBlue};
    }
    &:focus {
      border-color: ${COLORS.$mainBlue};
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
    }
  }
`;

export const SlideTransition = styled.div`
   max-height: ${({ isVisible }) => (isVisible ? '1000px' : '0')};
   opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
   transition: max-height 0.6s ease, opacity 0.6s ease;
   overflow: hidden;
 `;

export const SpotlightButton = styled.div`
 margin-top: auto;
 width: 100%;
 min-height: 55px;
 display: flex;
 align-items: center;
 justify-content: space-between;
 padding: 0 20px;
 text-decoration: none;

 border-radius: ${({ theme }) => {
    if (theme?.button_style?.includes('completely-rounded')) return '27.5px';
    if (theme?.button_style?.includes('slightly-rounded')) return '10px';
    return '0';
  }};

 background-color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? 'transparent'
      : theme?.button_color || COLORS.$mainBlue};

 border: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? `2px solid ${theme?.card_color || COLORS.$mainBlue}`
      : 'none'} !important;

 color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? theme?.button_color || COLORS.$mainBlue
      : theme?.button_text_color || '#FFFFFF'} !important;

 transition: all 0.3s ease;

 /* Become semi-transparent if isTipOpen = true */
 opacity: ${({ isTipOpen }) => (isTipOpen ? '0.3' : '1')};

 &:hover {
   background-color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? theme?.button_color || COLORS.$mainBlue
      : `${theme?.button_color || COLORS.$mainBlue}dd`} !important;
   color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? 'transparent'
      : theme?.button_text_color || '#FFFFFF'} !important;
 }

 &:focus,
 &:active {
   background-color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? 'transparent'
      : theme?.button_color || COLORS.$mainBlue} !important;
   color: ${({ theme }) =>
    theme?.button_style?.includes('outline')
      ? theme?.button_color || COLORS.$mainBlue
      : theme?.button_text_color || '#FFFFFF'} !important;
   border-color: ${({ theme }) => theme?.button_color || COLORS.$mainBlue} !important;
 }
`;